import {
  EmployeeInvitationStatusTypes,
  SubscriberProgress,
  SubscriberIMQProgress,
  SubscribersFilter,
} from 'types/electionService';
import { getSubscribersPageForGroup } from 'api/operations/electionService';
import { applyProposalUserAction } from 'api/operations/enrollmentService';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';
import { FiltersObject } from 'store/general/paginationDelegate';
import IAddEmployeesPage from 'store/modules/add-employees/iAddEmployeesPage';
import { USER_ACTIONS } from 'types/enrollmentService';
import { getStandardActionsWithSelfEnrollFeature } from 'store/modules/add-employees/inviteEmployeeActions';

interface DomainParams {
  periodId: string;
  groupId: string;
  cartId: string;
}

interface MonitorAddEmployeesFilters extends FiltersObject {
  status: EmployeeInvitationStatusTypes | null;
  subProgress: SubscriberIMQProgress | null;
  employeeName: string;
}

export default class MonitorAddEmployeesPageStore extends IAddEmployeesPage<MonitorAddEmployeesFilters> {
  protected _domainParams: DomainParams;

  public get stepCanComplete(): boolean {
    return (
      this.employees.length > 0 &&
      this.employees.every((employee) => employee.rawData.progress === SubscriberProgress.COMPLETED)
    );
  }

  public async getEmployees(): Promise<void> {
    this._setIsLoading(true);
    const { result, totalElements, totalPages } = await getSubscribersPageForGroup({
      groupId: this._domainParams.groupId,
      cartId: this._domainParams.cartId,
      sortBy: this.paginationDelegate.sortBy,
      sortOrder: this.paginationDelegate.sortOrder,
      status: this.paginationDelegate.filters.status ? [this.paginationDelegate.filters.status] : [],
      addOnMethod: [],
      progress: this.paginationDelegate.filters.subProgress ? [this.paginationDelegate.filters.subProgress] : [],
      filter: SubscribersFilter.ALL,
      page: this.paginationDelegate.page,
      name: this.paginationDelegate.filters.employeeName,
    });
    runInAction(() => {
      this._createEmployeeDelegates(result);
      this.paginationDelegate.setTotalElements(totalElements);
      this.paginationDelegate.setTotalPages(totalPages);
      this._setIsLoading(false);
    });
  }

  public async onComplete(): Promise<void> {
    this._setIsLoading(true);
    await applyProposalUserAction(
      this._domainParams.periodId,
      this._domainParams.cartId,
      USER_ACTIONS.COMPLETE_ADD_EMPLOYEES,
    );
    runInAction(() => {
      this._setIsLoading(false);
      this.onBack();
    });
  }

  public onBack(): void {
    bpHistory.push(`/rfq-checklist/${this._domainParams.periodId}?cartId=${this._domainParams.cartId}`);
  }

  constructor(params: DomainParams) {
    super({ status: null, employeeName: '', subProgress: null }, getStandardActionsWithSelfEnrollFeature(), params);
    this._domainParams = params;
    makeObservable<MonitorAddEmployeesPageStore, '_domainParams'>(this, {
      _domainParams: observable,
      stepCanComplete: computed,
      getEmployees: action.bound,
      onComplete: action.bound,
      onBack: action.bound,
    });
  }
}

const MonitorAddEmployeesPageStoreContext = createContext(
  new MonitorAddEmployeesPageStore({
    groupId: 'stub-group-id',
    cartId: 'stub-cart-id',
    periodId: 'stub-enrollment-id',
  }),
);

export const useMonitorAddEmployeesPageStore = (): MonitorAddEmployeesPageStore => {
  return useContext(MonitorAddEmployeesPageStoreContext);
};

export { MonitorAddEmployeesPageStore, MonitorAddEmployeesPageStoreContext };
